
const wordLo = (word) => {
    return word & 0xff;
};

const wordHi = (word) => {
    return (word & 0xff00) >> 8;
};


export default function amber(opts) {
    const glyphPixelWidth = 8;
    const glyphPixelHeight = 16;
    const glyphCount = 256;
    const glyphDataLength = ((glyphPixelWidth * glyphPixelHeight) / 8);
    const glyphCanvasDataLength = glyphDataLength * 32; // monochrome -> RGBA
    const glyphBufferLength = glyphDataLength * glyphCount;
    const glyphCanvasBufferLength = glyphCanvasDataLength * glyphCount;
    const characterPixelWidth = 9;
    const characterPixelHeight = 16;
    const bitmapWidth = characterPixelWidth * opts.columns;
    const bitmapHeight = characterPixelHeight * opts.rows;
    const bitmapPixels = bitmapWidth * bitmapHeight;
    const textBufferLength = opts.rows * opts.columns;
    const textBuffer = Buffer.alloc(textBufferLength);
    const glyphBuffer = Buffer.alloc(glyphBufferLength);
    const glyphCanvasBuffer = Buffer.alloc(glyphCanvasBufferLength);
    const onColor = Buffer.from([ 255, 204, 50, 255 ]);
    const offColor = Buffer.from([ 0, 0, 0, 255 ]);
    let statusReg = 0;
    if (opts.glyphBuffer) {
        opts.glyphBuffer.copy(glyphBuffer);
    }
    const cursor = {
        x: 0,
        y: 0,
        lin: 0,
        on: 0,
        startPixel: 0,
        endPixel: 0,
        onTime: 0,
        offset: 0,
        updateX(val) {
            this.x = val;
            this.calcLin();
        },
        updateY(val) {
            this.y = val;
            this.calcLin();
        },
        updateLinLo(val) {
            this.lin = (this.lin & 0xff00) | val;
            this.calcCoords();
        },
        updateLinHi(val) {
            this.lin = (this.lin & 0x00ff) | (val << 8);
            this.calcCoords();
        },
        updateOffsetLo(val) {
            this.offset = (this.offset & 0xff00) | val;
        },
        updateOffsetHi(val) {
            this.offset = (this.offset & 0x00ff) | (val << 8);
        },
        calcLin() {
            this.lin = (this.y * opts.columns) + this.x;
        },
        calcCoords() {
            this.y = Math.trunc(this.lin / opts.columns);
            this.x = this.lin % opts.columns;
        }
    };
    let lastImageData;
    let dirty = true;
    let glyphsDirty = true;
    let frame = 0;

    const writeStatusReg = (value) => {
        statusReg = value;
        opts.interruptLevelChanged((statusReg & 0x80) > 0 );
    };

    const buildGlyphCanvasBuffer = () => {
        let canvasBufferPos = 0;
        for (let i = 0; i < glyphBufferLength; i++) {
            let dataByte = glyphBuffer[i];
            for (let bit = 0; bit < 8; bit++) {
                const color = ((dataByte & (1 << (7-bit))) > 0) ? onColor : offColor;
                color.copy(glyphCanvasBuffer, canvasBufferPos);
                canvasBufferPos += 4;
            }
        }
    };

    const displayGlyph = (buf, char, pixelX, pixelY) => {
        if (char == 32) {
            return;
        }
        let glyphPos = char * glyphCanvasDataLength;
        let bitmapPos = (pixelY * bitmapWidth * 4) + (pixelX * 4);
        let glyphRowByteLength = glyphPixelWidth * 4;
        for (let glyphRow = 0; glyphRow < glyphPixelHeight; glyphRow++) {
            glyphCanvasBuffer.copy(buf, bitmapPos, glyphPos, glyphPos + glyphRowByteLength);
            glyphPos += glyphRowByteLength;
            bitmapPos += bitmapWidth * 4;
        }
    };

    const buildImageData = () => {
        const buf = Buffer.alloc(bitmapPixels * 4);
        buf.fill(offColor);

        let textIdx = 0;
        for (let charY = 0; charY < opts.rows; charY++) {
            for (let charX = 0; charX < opts.columns; charX++) {
                let pixelX = charX * characterPixelWidth;
                let pixelY = charY * characterPixelHeight;
                displayGlyph(buf, textBuffer[textIdx++], pixelX, pixelY);
            }
        }

        if (cursor.on && (frame < cursor.onTime) && (cursor.x < opts.columns) && (cursor.y < opts.rows)) {
            let pixelX = cursor.x * characterPixelWidth;
            let pixelY = cursor.y * characterPixelHeight;
            let bitmapPos = (pixelY * bitmapWidth * 4) + (pixelX * 4);
            for (let i = 0; i < characterPixelHeight; i++) {
                if (i >= cursor.startPixel && i <= cursor.endPixel) {
                    buf.fill(onColor, bitmapPos, bitmapPos + (glyphPixelWidth * 4));
                }
                bitmapPos += bitmapWidth * 4;
            }
        }

        const imageData = new ImageData(new Uint8ClampedArray(buf), bitmapWidth, bitmapHeight);
        return imageData;
    };

    const getImage = () => {
        if (++frame >= opts.frameRate) {
            frame = 0;
            if (cursor.on) {
                dirty = true;
            }
        }

        if (cursor.on && (frame == cursor.onTime)) {
            dirty = true;
        }

        if (glyphsDirty) {
            glyphsDirty = false;
            dirty = true;
            buildGlyphCanvasBuffer();
        }

        let wasDirty = dirty;
        if (dirty) {
            dirty = false;
            lastImageData = buildImageData();
        }

        writeStatusReg(statusReg | 0x80); // bit 7: vert retrace

        return {
            imageData: lastImageData,
            dirty: wasDirty
        };
    };

    const readRegister = (reg) => {
        switch (reg) {
            case 0:     return cursor.x;
            case 1:     return cursor.y;
            case 2:     return wordLo(cursor.lin);
            case 3:     return wordHi(cursor.lin);
            case 4:     return cursor.on;
            case 5:     return cursor.startPixel;
            case 6:     return cursor.endPixel;
            case 7:     return cursor.onTime;
            case 10:    return statusReg;
            case 24:    return opts.frameRate;
            case 25:    return opts.columns;
            case 26:    return opts.rows;
            case 27:    return glyphPixelWidth;
            case 28:    return glyphPixelHeight;
            case 29:    return wordLo(cursor.offset + cursor.lin);
            case 30:    return wordHi(cursor.offset + cursor.lin);
            default:    return 0;
        }
    };

    const writeRegister = (reg, value) => {
        switch (reg) {
            case 0:     cursor.updateX(value); dirty = true; break;
            case 1:     cursor.updateY(value); dirty = true; break;
            case 2:     cursor.updateLinLo(value); dirty = true; break;
            case 3:     cursor.updateLinHi(value); dirty = true; break;
            case 4:     cursor.on = value; dirty = true; break;
            case 5:     cursor.startPixel = value;dirty = true; break;
            case 6:     cursor.endPixel = value; dirty = true; break;
            case 7:     cursor.onTime = value; dirty = true; break;
            case 8:     cursor.updateOffsetLo(value); break;
            case 9:     cursor.updateOffsetHi(value); break;
            case 10:    writeStatusReg(value); break;
            default:    break;
        }
    };

    const readUInt8 = (address) => {
        if (address < 0x07d0) {
            return textBuffer[address];
        } else if (address < 0x0800) {
            return readRegister(address - 0x07d0);
        } else {
            return glyphBuffer[address - 0x0800];
        }
    };

    const writeUInt8 = (value, address) => {
        if (address < 0x07d0) {
            if (textBuffer[address] != value) {
                textBuffer[address] = value;
                dirty = true;
            }
        } else if (address < 0x0800) {
            writeRegister(address - 0x07d0, value);
        } else {
            if (glyphBuffer[address - 0x0800] != value) {
                glyphBuffer[address - 0x0800] = value;
                glyphsDirty = true;
            }
        }
    };

    const reset = () => {
        textBuffer.fill(0x00);
        dirty = true;
    };

    return {
        getImage: getImage,
        readUInt8: readUInt8,
        writeUInt8: writeUInt8,
        bitmapWidth: bitmapWidth,
        bitmapHeight: bitmapHeight,
        reset: reset,

        read(address) {
            return readUInt8(address);
        },
        write(address, value) {
            return writeUInt8( value, address);
        }
    };
};



// WEBPACK FOOTER //
// ./src/emu/amber.js